import { Pause, Play, Repeat, RotateCcw, SkipBack, Volume2, VolumeX } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAnimation } from "@/features/animation";
import { useProject } from "@/features/project";

export const Timeline: React.FC = () => {
  const { stableSnapshot, updateProject } = useProject();
  const { controller, isPlaying, isLooping, isMuted, volume, progress } = useAnimation();

  const animDuration = stableSnapshot.duration || 3;

  // Refs for DOM elements we update directly (avoid React re-renders)
  const timeDisplayRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLInputElement>(null);

  // Duration editing state
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [durationInput, setDurationInput] = useState(animDuration.toFixed(2));
  const durationInputRef = useRef<HTMLInputElement>(null);

  // Volume slider visibility
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Update input when duration prop changes (from store)
  useEffect(() => {
    if (!isEditingDuration) {
      setDurationInput(animDuration.toFixed(2));
    }
  }, [animDuration, isEditingDuration]);

  // Sync duration to controller when it changes from store
  useEffect(() => {
    controller.setDuration(animDuration);
  }, [animDuration, controller]);

  // Subscribe to controller updates and update DOM directly for smooth animation
  useEffect(() => {
    const unsubscribe = controller.subscribe((state) => {
      // Update DOM directly for smooth animation
      if (timeDisplayRef.current) {
        const currentTime = state.currentTimeMs / 1000;
        timeDisplayRef.current.textContent = `${currentTime.toFixed(2)}s`;
      }
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${state.progress * 100}%`;
      }
      if (thumbRef.current) {
        thumbRef.current.style.left = `${state.progress * 100}%`;
      }
    });

    return unsubscribe;
  }, [controller]);

  const handlePlayPause = () => {
    controller.togglePlay();
  };

  const handleReset = () => {
    controller.reset();
  };

  const handleToggleLoop = () => {
    const newLooping = !controller.isLooping;
    controller.setLooping(newLooping);
  };

  const handleToggleMute = () => {
    controller.setMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    controller.setVolume(newVolume);
  };

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seconds = parseFloat(e.target.value);
    controller.seek(seconds * 1000);
  };

  const handleDurationClick = () => {
    setIsEditingDuration(true);
    setTimeout(() => {
      durationInputRef.current?.select();
    }, 0);
  };

  const commitDuration = useCallback(() => {
    const parsed = parseFloat(durationInput);
    if (!Number.isNaN(parsed) && parsed > 0) {
      const newDuration = Math.max(0.1, parsed); // Minimum 0.1s, no upper limit
      updateProject({ duration: newDuration });
      setDurationInput(newDuration.toFixed(2));
    } else {
      setDurationInput(animDuration.toFixed(2));
    }
    setIsEditingDuration(false);
  }, [durationInput, animDuration, updateProject]);

  const handleDurationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitDuration();
    } else if (e.key === "Escape") {
      setDurationInput(animDuration.toFixed(2));
      setIsEditingDuration(false);
    }
  };

  const isEnded = progress >= 1 && !isLooping;
  const sliderValue = isMuted ? 0 : volume;

  // Dimensions scale with min(width, height) using CSS custom properties
  // Uses min() to pick the smaller of width-based or height-based scaling
  const cssVars = {
    "--tl-height": "clamp(48px, min(calc(8 * var(--vh)), 7vw), 64px)",
    "--tl-padding": "clamp(8px, min(calc(1.5 * var(--vh)), 1.8vw), 16px)",
    "--tl-gap": "clamp(8px, min(calc(1.5 * var(--vh)), 1.8vw), 16px)",
    "--tl-btn-size": "clamp(32px, min(calc(5 * var(--vh)), 4.5vw), 40px)",
    "--tl-btn-padding": "clamp(4px, min(calc(0.75 * var(--vh)), 0.9vw), 8px)",
    "--tl-time-width": "clamp(40px, min(calc(6 * var(--vh)), 5.5vw), 48px)",
  } as React.CSSProperties;

  return (
    <div
      className="bg-slate-950 border-t border-slate-800 flex items-center select-none z-30 relative"
      style={{
        ...cssVars,
        height: "var(--tl-height)",
        paddingLeft: "var(--tl-padding)",
        paddingRight: "var(--tl-padding)",
        gap: "var(--tl-gap)",
      }}
    >
      {/* Playback Controls */}
      <div className="flex items-center shrink-0" style={{ gap: "calc(var(--tl-gap) / 2)" }}>
        <button
          onClick={handleReset}
          className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-all"
          style={{ padding: "var(--tl-btn-padding)" }}
          title="Reset"
        >
          <SkipBack size={14} aria-label="Reset" />
        </button>

        <button
          onClick={handlePlayPause}
          className={`flex items-center justify-center rounded-full shadow-lg transition-all active:scale-95 ${
            isEnded
              ? "bg-emerald-600 hover:bg-emerald-500 text-white"
              : "bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-900/20"
          }`}
          style={{ width: "var(--tl-btn-size)", height: "var(--tl-btn-size)" }}
        >
          {isPlaying ? (
            <Pause size={14} fill="currentColor" aria-label="Pause" />
          ) : isEnded ? (
            <RotateCcw size={14} aria-label="Restart" />
          ) : (
            <Play size={14} fill="currentColor" className="ml-0.5" aria-label="Play" />
          )}
        </button>

        <button
          onClick={handleToggleLoop}
          className={`rounded-full transition-all ${
            isLooping ? "text-emerald-400 bg-emerald-950/50" : "text-slate-500 hover:text-slate-300"
          }`}
          style={{ padding: "var(--tl-btn-padding)" }}
          title="Loop"
        >
          <Repeat size={14} aria-label="Loop" />
        </button>
      </div>

      {/* Time Display & Scrubber */}
      <div className="flex-1 flex items-center min-w-0" style={{ gap: "calc(var(--tl-gap))" }}>
        <div
          ref={timeDisplayRef}
          className="font-mono text-xs text-indigo-400 text-right shrink-0"
          style={{ width: "var(--tl-time-width)" }}
        >
          0.00s
        </div>

        <div className="flex-1 relative h-8 flex items-center group min-w-0">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
              <div
                ref={progressBarRef}
                className="h-full bg-indigo-600/50 group-hover:bg-indigo-500 transition-colors"
                style={{ width: "0%" }}
              />
            </div>
          </div>
          <input
            type="range"
            min="0"
            max={animDuration}
            step="0.01"
            defaultValue={0}
            onChange={handleScrub}
            className="relative w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div
            ref={thumbRef}
            className="absolute h-3 w-3 bg-white rounded-full shadow pointer-events-none transition-transform group-hover:scale-125"
            style={{ left: "0%", transform: "translateX(-50%)" }}
          />
        </div>

        {isEditingDuration ? (
          <input
            ref={durationInputRef}
            type="text"
            value={durationInput}
            onChange={(e) => setDurationInput(e.target.value)}
            onBlur={commitDuration}
            onKeyDown={handleDurationKeyDown}
            className="font-mono text-xs text-white bg-slate-800 border border-indigo-500 rounded px-1 w-11 sbs:w-14 text-center outline-none shrink-0"
            autoFocus
          />
        ) : (
          <button
            onClick={handleDurationClick}
            className="font-mono text-xs text-slate-500 hover:text-indigo-400 hover:bg-slate-800 rounded px-1 w-11 sbs:w-14 text-center transition-colors cursor-text shrink-0"
            title="Click to edit duration"
          >
            {animDuration.toFixed(2)}s
          </button>
        )}

        {/* Volume Control */}
        <div
          className="relative shrink-0"
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <button
            onClick={handleToggleMute}
            className={`rounded-full transition-all ${
              isMuted
                ? "text-rose-400 bg-rose-950/50"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
            style={{ padding: "var(--tl-btn-padding)" }}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted || volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>

          {/* Volume Slider Popup - Vertical, connected to button */}
          {showVolumeSlider && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 pb-0 flex flex-col items-center">
              {/* Slider container */}
              <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 shadow-xl">
                <input
                  ref={volumeSliderRef}
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={sliderValue}
                  onChange={handleVolumeChange}
                  className="h-20 w-1 bg-slate-700 rounded-full appearance-none cursor-pointer accent-indigo-500"
                  style={{
                    writingMode: "vertical-lr",
                    direction: "rtl",
                    background: `linear-gradient(to top, rgb(99 102 241) ${sliderValue * 100}%, rgb(51 65 85) ${sliderValue * 100}%)`,
                  }}
                />
              </div>
              {/* Invisible bridge to connect popup to button */}
              <div className="h-1 w-6" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
